import { AlertDialog } from '@base-ui/react/alert-dialog'
import { useRef, type ReactNode, type RefObject } from 'react'
import { Button } from './button'

/**
 * A question to confirm before an action that cannot be undone or loses something (WAI-ARIA
 * alertdialog): its title, what will happen, and two buttons. The focus starts on the way back
 * («Cancelar»), so that a key pressed by habit does not confirm it; Escape closes it, a click
 * outside does not. While the action runs, the confirm button says so and waits, and the
 * dialog stays open; a refusal shows above the buttons.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  children,
  confirmLabel,
  busyLabel,
  cancelLabel,
  danger = false,
  busy = false,
  error,
  onConfirm,
  opener,
  fallbackFocus,
  onClosed,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** What will happen, under the title. */
  children: ReactNode
  confirmLabel: string
  busyLabel?: string
  cancelLabel: string
  /** The action removes or ends something: the confirm button says so. */
  danger?: boolean
  busy?: boolean
  /** Why the action was refused, already translated. */
  error?: string | null
  onConfirm: () => void
  /** Where the focus goes back once it closes, if it is still on the page. */
  opener?: RefObject<HTMLElement | null>
  /**
   * Where the focus goes instead when the opener is gone (the action done removed it), so that
   * it never falls to the page's body: what says the action is done.
   */
  fallbackFocus?: RefObject<HTMLElement | null>
  /**
   * Once it has closed, as it unmounts: the next step may open a dialog of its own. The focus goes
   * back right after this: what takes it waits for the next frame.
   */
  onClosed?: () => void
}) {
  const cancel = useRef<HTMLButtonElement>(null)
  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next)
      }}
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) onClosed?.()
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className="fixed inset-0 z-[70] bg-black/40" />
        <AlertDialog.Viewport className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4 sm:items-center">
          <AlertDialog.Popup
            initialFocus={cancel}
            finalFocus={() =>
              opener?.current?.isConnected
                ? opener.current
                : fallbackFocus?.current?.isConnected
                  ? fallbackFocus.current
                  : true
            }
            className="w-full max-w-lg rounded-card bg-surface p-6 shadow-xl outline-none"
          >
            <AlertDialog.Title className="text-xl [overflow-wrap:anywhere]">
              {title}
            </AlertDialog.Title>
            <AlertDialog.Description render={<div />} className="mt-3 space-y-3 text-sm">
              {children}
            </AlertDialog.Description>
            <p role="alert" className="text-sm text-danger not-empty:mt-4">
              {error ?? ''}
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <AlertDialog.Close
                render={
                  <Button ref={cancel} variant="secondary">
                    {cancelLabel}
                  </Button>
                }
              />
              <Button
                variant={danger ? 'danger' : 'primary'}
                // While it runs the button says so and waits, keeping the focus.
                aria-disabled={busy || undefined}
                className="aria-disabled:cursor-progress aria-disabled:opacity-50"
                onClick={() => {
                  if (!busy) onConfirm()
                }}
              >
                {busy && busyLabel ? busyLabel : confirmLabel}
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Viewport>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
